if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.originalMainSlides = null;
        this.originalThumbnails = null;
        this.currentFilteredVariant = null;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.filterThumbnails = this.dataset.filterThumbnails === 'true';
        this.variantMediaMap = {};
        if (this.dataset.variantMediaMap) {
          try {
            this.variantMediaMap = JSON.parse(this.dataset.variantMediaMap);
          } catch (e) {
            console.warn('Error parsing variantMediaMap:', e);
          }
        }
        
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        /* Filter media for initially selected variant */
        if (this.filterThumbnails) {
          this.filterInitialMedia();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.concealLoopDuplicates();
        }
      }

      concealLoopDuplicates() {
        this.querySelectorAll('.swiper-slide-duplicate').forEach((slide) => {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
        });
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        const dataSource = this.buildImageZoomDataSource();

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            /* Map lightbox index back to slider index */
            const filteredIndex = this.mapLightboxIndexToSliderIndex(currIndex);
            if (filteredIndex >= 0) {
              this._navigateSlider(this.sliderInstance.slider, filteredIndex);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          /* Check dataSource length to determine if we should show navigation */
          const hasMultipleItems = (this.lightbox.options.dataSource?.length || 0) > 1;
          
          /* Only register prev/next buttons if we have multiple items and it's not only images */
          if (!this.onlyImage && hasMultipleItems) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          
          /* Always register close button */
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.on('afterInit', () => {
          /* Set initial navigation visibility based on dataSource length */
          if (this.lightbox.pswp) {
            this.toggleLightboxNavigation(this.lightbox.options.dataSource?.length || 0);
          }
        });

        this.lightbox.init();

        this.lightbox.on('beforeOpen', () => {
          /* Update navigation visibility before opening */
          requestAnimationFrame(() => {
            this.toggleLightboxNavigation(this.lightbox.options.dataSource?.length || 0);
          });
        });

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              /* Map the original media index to the filtered lightbox index */
              const originalIndex = Number(media.dataset.mediaIndex) || 0;
              const filteredIndex = this.mapOriginalIndexToLightboxIndex(originalIndex);
              if (filteredIndex >= 0) {
                this.lightbox.loadAndOpen(filteredIndex);
              } else {
                console.warn('⚠️ [Image Zoom] Could not find filtered index for original index:', originalIndex);
              }
            }
          },
        });
      }

      buildImageZoomDataSource() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }
        return dataSource;
      }

      updateImageZoomDataSource() {
        if (!this.lightbox) {
          console.warn('⚠️ [Image Zoom] Lightbox not initialized, cannot update dataSource');
          return;
        }

        const newDataSource = this.buildImageZoomDataSource();
        
        /* Update the lightbox dataSource */
        this.lightbox.options.dataSource = newDataSource;
        
        /* If lightbox is already initialized, we need to update it */
        if (this.lightbox.pswp) {
          this.lightbox.pswp.contentLoader.dataSource = newDataSource;
          /* Toggle navigation buttons based on item count */
          requestAnimationFrame(() => {
            this.toggleLightboxNavigation(newDataSource.length);
          });
        }
      }

      toggleLightboxNavigation(itemCount) {
        if (!this.lightbox?.pswp) {
          return;
        }

        const hasMultipleItems = itemCount > 1;
        
        /* Query the PhotoSwipe container element directly */
        const pswpElement = this.lightbox.pswp.element;
        if (!pswpElement) {
          return;
        }
        
        /* Find buttons by class in the PhotoSwipe container */
        const prevBtn = pswpElement.querySelector('.pswp__button--prev');
        const nextBtn = pswpElement.querySelector('.pswp__button--next');
        
        if (prevBtn) {
          prevBtn.style.display = hasMultipleItems ? '' : 'none';
        }
        
        if (nextBtn) {
          nextBtn.style.display = hasMultipleItems ? '' : 'none';
        }
        
        /* Also try UI API as fallback */
        if (this.lightbox.pswp.ui) {
          const prevButton = this.lightbox.pswp.ui.getElement('prev');
          const nextButton = this.lightbox.pswp.ui.getElement('next');
          
          if (prevButton) {
            prevButton.style.display = hasMultipleItems ? '' : 'none';
          }
          
          if (nextButton) {
            nextButton.style.display = hasMultipleItems ? '' : 'none';
          }
        }
      }

      mapOriginalIndexToLightboxIndex(originalIndex) {
        /* Find the media item with the original index */
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const targetMedia = allMedia.find(media => Number(media.dataset.mediaIndex) === originalIndex);
        
        if (!targetMedia) {
          return -1;
        }
        
        /* Find its position in the filtered array */
        return allMedia.indexOf(targetMedia);
      }

      mapLightboxIndexToSliderIndex(lightboxIndex) {
        /* Get the media item at the lightbox index */
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const targetMedia = allMedia[lightboxIndex];
        
        if (!targetMedia || !this.sliderInstance?.slider) {
          return -1;
        }
        
        /* Find which slider slide contains this media item */
        const slides = this.sliderInstance.slider.slides;
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          if (!slide.classList.contains('swiper-slide-duplicate')) {
            const slideMedia = slide.querySelector('.product__media-item');
            if (slideMedia === targetMedia) {
              return i;
            }
          }
        }
        
        return -1;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          /* Ensure navigation arrows & pagination are correct on initial load */
          const realSlides = Array.from(swiper.slides).filter(
            (slide) => !slide.classList.contains('swiper-slide-duplicate')
          );
          this.toggleNavigationArrows(realSlides.length);
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) {
          console.warn('⚠️ [Variant Filter] setActiveMedia called with no variant');
          return;
        }

        /* Filter thumbnails first if enabled */
        if (this.filterThumbnails) {
          this.filterThumbnailsByVariant(variant.id);
        }

        /* Then handle main media switching */
        if (!variant.hasOwnProperty('featured_media') || !variant.featured_media) {
          console.warn('⚠️ [Variant Filter] Variant has no featured_media, returning early');
          return;
        }

        /* Wait for slider to be ready after filtering */
        const setActiveSlide = () => {
          if (this.sliderInstance?.slider) {
            /* Ensure slider is updated before navigation */
            this.sliderInstance.slider.update();
            
            /* Wait for Swiper to finish layout calculations */
            requestAnimationFrame(() => {
              const featuredMediaId = variant.featured_media.id;
              
              /* Find the media item in the current filtered DOM */
              const targetMediaItem = this.elements.mediaList.querySelector(
                `.product__media-item[data-media-id="${featuredMediaId}"]`
              );
              
              if (targetMediaItem) {
                /* Find which Swiper slide contains this media item */
                let targetIndex = -1;
                const slides = this.sliderInstance.slider.slides;
                
                slides.forEach((slide, index) => {
                  const mediaItem = slide.querySelector('.product__media-item');
                  if (mediaItem === targetMediaItem && targetIndex === -1) {
                    /* Skip duplicates */
                    if (!slide.classList.contains('swiper-slide-duplicate')) {
                      targetIndex = index;
                    }
                  }
                });
                
                if (targetIndex >= 0) {
                  this._navigateSlider(this.sliderInstance.slider, targetIndex);
                  
                  /* Ensure thumbnail is synced - wait a frame after slideTo */
                  requestAnimationFrame(() => {
                    this.syncThumbnailActiveState(featuredMediaId);
                  });
                } else {
                  console.warn('⚠️ [Variant Filter] Found media item but could not find corresponding Swiper slide');
                }
              } else {
                console.warn('⚠️ [Variant Filter] Could not find media item with featured_media.id:', featuredMediaId);
                /* Fallback to position-based navigation */
                const slideIndex = variant.featured_media.position || 0;
                if (slideIndex > 0) {
                  this._navigateSlider(this.sliderInstance.slider, slideIndex - 1);
                  
                  /* Sync thumbnail after fallback navigation */
                  requestAnimationFrame(() => {
                    this.syncThumbnailActiveState(featuredMediaId);
                  });
                }
              }
            });
          } else {
            this.sortMediaItems(variant);
          }
        };
        
        /* If filtering was done, wait a bit for slider to reinitialize */
        if (this.filterThumbnails) {
          setTimeout(setActiveSlide, 150);
        } else {
          setActiveSlide();
        }
      }
      
      syncThumbnailActiveState(mediaId) {
        if (!this.elements.thumbnails) {
          console.warn('⚠️ [Variant Filter] Thumbnails container not available for syncing');
          return;
        }
        
        /* Wait for DOM to be ready */
        requestAnimationFrame(() => {
          const thumbnails = this.elements.thumbnails.querySelectorAll('.product__thumbs-item');
          
          if (thumbnails.length === 0) {
            console.warn('⚠️ [Variant Filter] No thumbnails found');
            return;
          }
          
          /* Remove active class from all thumbnails */
          thumbnails.forEach(thumb => {
            thumb.classList.remove('swiper-slide-thumb-active');
          });
          
          /* Find and activate the matching thumbnail */
          const targetThumb = Array.from(thumbnails).find(thumb => {
            const thumbMediaId = parseInt(thumb.dataset.mediaId || thumb.dataset.target);
            return thumbMediaId === parseInt(mediaId);
          });
          
          if (targetThumb) {
            targetThumb.classList.add('swiper-slide-thumb-active');
            
            /* Also update Swiper's internal state if slider exists */
            if (this.thumbsInstance?.slider) {
              const thumbIndex = Array.from(thumbnails).indexOf(targetThumb);
              if (thumbIndex >= 0) {
                this.thumbsInstance.slider.slideTo(thumbIndex, 0, false);
              }
            }
          } else {
            console.warn('⚠️ [Variant Filter] Could not find thumbnail for media:', mediaId);
          }
        });
      }

      toggleNavigationArrows(slideCount) {
        const navPrev = this.querySelector('.swiper-button-prev');
        const navNext = this.querySelector('.swiper-button-next');
        
        if (slideCount <= 1) {
          if (navPrev) navPrev.style.display = 'none';
          if (navNext) navNext.style.display = 'none';
        } else {
          if (navPrev) {
            navPrev.style.display = '';
            navPrev.classList.remove('swiper-button-lock');
            navPrev.removeAttribute('disabled');
          }
          if (navNext) {
            navNext.style.display = '';
            navNext.classList.remove('swiper-button-lock');
            navNext.removeAttribute('disabled');
          }
        }
        
        /* Hide pagination when only 1 slide */
        const pagination = this.querySelector('.swiper-pagination');
        if (pagination) {
          pagination.classList.toggle('pagination-hidden', slideCount <= 1);
        }
      }

      _updateProductMediaClasses(slideCount) {
        const productElement = this.closest('.product');
        if (!productElement) {
          return;
        }

        /* Remove all media count classes */
        const classList = productElement.classList;
        Array.from(classList).forEach((className) => {
          if (className.startsWith('product--media-has-')) {
            classList.remove(className);
          }
        });

        /* Add appropriate class based on slide count */
        if (slideCount === 1) {
          productElement.classList.add('product--media-has-1');
        } else if (slideCount > 1) {
          productElement.classList.add(`product--media-has-${slideCount}`);
          productElement.classList.add('product--media-has-many');
        }
      }

      _navigateSlider(slider, index) {
        if (slider.params.loop) {
          slider.slideToLoop(index, 0, false);
        } else {
          slider.slideTo(index, 0, false);
        }
      }

      _storeOriginalSlides() {
        /* Store original slides on first run - exclude Swiper duplicates */
        if (!this.originalMainSlides && this.elements.mediaList) {
          this.originalMainSlides = Array.from(
            this.elements.mediaList.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
          );
        }

        if (!this.originalThumbnails && this.elements.thumbnails) {
          this.originalThumbnails = Array.from(
            this.elements.thumbnails.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)')
          );
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      filterThumbnailsByVariant(variantId) {
        if (!this.filterThumbnails) {
          return;
        }

        /* Skip if already filtered to this variant */
        if (this.currentFilteredVariant === variantId) {
          return;
        }
        this.currentFilteredVariant = variantId;

        this._storeOriginalSlides();

        /* Convert variantId to string for map lookup (JSON keys are strings) */
        const variantIdStr = String(variantId);
        const variantMediaIds = this.variantMediaMap[variantIdStr];

        /* If variant is in map but has no media, show first slide only as fallback */
        if (variantIdStr in this.variantMediaMap && (!variantMediaIds || variantMediaIds.length === 0)) {
          this.showFirstSlideOnly();
          return;
        }

        /* If variant not in map at all, show all slides */
        if (!variantMediaIds || variantMediaIds.length === 0) {
          this.showAllMedia();
          return;
        }

        /* Convert variantMediaIds to numbers for comparison */
        const variantMediaIdsNumeric = variantMediaIds.map(id => parseInt(id));

        /* Destroy both sliders first */
        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (this.thumbsInstance?.slider) {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }

        /* Filter main slides DOM (without reinitializing) */
        const mainSlideCount = this.filterMainSlidesDOM(variantMediaIdsNumeric);

        /* Filter thumbnails DOM (without reinitializing) */
        this.filterThumbnailSlidesDOM(variantMediaIdsNumeric);

        /* Now initialize both sliders together */
        this.reinitializeSliders(mainSlideCount);
      }

      filterMainSlidesDOM(variantMediaIdsNumeric) {
        if (!this.elements.mediaList || !this.originalMainSlides) {
          console.warn('⚠️ [Variant Filter] mediaList or originalMainSlides not found');
          return 0;
        }

        /* Remove all current slides */
        this.elements.mediaList.innerHTML = '';

        /* Add back only matching slides */
        let addedCount = 0;
        this.originalMainSlides.forEach((originalSlide) => {
          const mediaId = parseInt(originalSlide.dataset.mediaId);
          if (variantMediaIdsNumeric.includes(mediaId)) {
            /* Clone the slide to avoid issues with multiple references */
            const clonedSlide = originalSlide.cloneNode(true);
            this.elements.mediaList.appendChild(clonedSlide);
            addedCount++;
          }
        });

        return addedCount;
      }

      filterThumbnailSlidesDOM(variantMediaIdsNumeric) {
        if (!this.elements.thumbnails || !this.originalThumbnails) {
          console.warn('⚠️ [Variant Filter] thumbnails or originalThumbnails not found');
          return;
        }

        /* Get thumbnail wrapper */
        const thumbnailWrapper = this.elements.thumbnails.querySelector('.swiper-wrapper');
        if (!thumbnailWrapper) {
          console.warn('⚠️ [Variant Filter] Thumbnail wrapper not found');
          return;
        }

        /* Remove all current thumbnails */
        thumbnailWrapper.innerHTML = '';

        /* Add back only matching thumbnails */
        let addedCount = 0;
        this.originalThumbnails.forEach((originalThumb) => {
          const mediaId = parseInt(originalThumb.dataset.mediaId || originalThumb.dataset.target);
          if (variantMediaIdsNumeric.includes(mediaId)) {
            /* Clone the thumbnail to avoid issues with multiple references */
            const clonedThumb = originalThumb.cloneNode(true);
            thumbnailWrapper.appendChild(clonedThumb);
            addedCount++;
          }
        });

        /* Re-attach click handlers to cloned thumbnails */
        const newThumbnails = thumbnailWrapper.querySelectorAll('.product__thumbs-item');
        newThumbnails.forEach((thumb) => {
          thumb.addEventListener('click', (e) => {
            e.preventDefault();
            const mediaId = parseInt(thumb.dataset.mediaId || thumb.dataset.target);
            if (mediaId && this.sliderInstance?.slider) {
              /* Find the slide with this media ID */
              const slides = this.sliderInstance.slider.slides;
              for (let i = 0; i < slides.length; i++) {
                const slide = slides[i];
                if (!slide.classList.contains('swiper-slide-duplicate')) {
                  const mediaItem = slide.querySelector('.product__media-item');
                  if (mediaItem && parseInt(mediaItem.dataset.mediaId) === mediaId) {
                    this._navigateSlider(this.sliderInstance.slider, i);
                    this.syncThumbnailActiveState(mediaId);
                    break;
                  }
                }
              }
            }
          });
        });
      }

      reinitializeSliders(slideCount) {
        /* Set loop option before initialization based on slide count */
        this.sliderOptions.loop = (slideCount > 1);

        /* Update product class based on filtered slide count */
        this._updateProductMediaClasses(slideCount);

        /* Initialize sliders if needed */
        if (this.enableDesktopSlider || FoxTheme.config.mqlMobile) {
          /* Initialize main slider */
          this.initSlider();
          
          /* Wait for Swiper to finish initialization and layout calculations */
          requestAnimationFrame(() => {
            if (this.sliderInstance?.slider) {
              /* Update slider to ensure all calculations are complete */
              this.sliderInstance.slider.update();
              this.concealLoopDuplicates();
              
              /* Wait one more frame for update to complete */
              requestAnimationFrame(() => {
                /* Show/hide navigation arrows based on slide count */
                this.toggleNavigationArrows(slideCount);

                /* Sync thumbnail active state after initialization */
                if (this.sliderInstance?.slider && this.thumbsInstance?.slider) {
                  /* Try to get the active slide's media ID from main slider */
                  const activeIndex = this.sliderInstance.slider.activeIndex;
                  const activeSlide = this.sliderInstance.slider.slides[activeIndex];
                  let targetMediaId = null;
                  
                  if (activeSlide) {
                    const mediaItem = activeSlide.querySelector('.product__media-item');
                    if (mediaItem) {
                      targetMediaId = parseInt(mediaItem.dataset.mediaId);
                    }
                  }
                  
                  /* If no active slide found, use the first visible thumbnail */
                  if (!targetMediaId) {
                    const firstThumb = this.elements.thumbnails?.querySelector('.product__thumbs-item');
                    if (firstThumb) {
                      targetMediaId = parseInt(firstThumb.dataset.mediaId || firstThumb.dataset.target);
                    }
                  }
                  
                  /* Sync the active state if we have a media ID */
                  if (targetMediaId) {
                    this.syncThumbnailActiveState(targetMediaId);
                  }
                }
              });
            }
          });
        }
        
        /* Update image zoom dataSource after filtering */
        if (this.enableImageZoom && this.lightbox) {
          requestAnimationFrame(() => {
            this.updateImageZoomDataSource();
          });
        }
      }

      showAllMedia() {
        /* Reset filtered variant tracking */
        this.currentFilteredVariant = null;
        
        /* Restore all main gallery slides */
        if (this.elements.mediaList && this.originalMainSlides) {
          /* Destroy Swiper if it exists */
          if (this.sliderInstance?.slider) {
            this.sliderInstance.slider.destroy(true, true);
            this.sliderInstance = false;
          }

          /* Remove all current slides */
          this.elements.mediaList.innerHTML = '';

          /* Add back all original slides */
          this.originalMainSlides.forEach((originalSlide) => {
            const clonedSlide = originalSlide.cloneNode(true);
            this.elements.mediaList.appendChild(clonedSlide);
          });

          /* Set loop option before initialization based on slide count */
          const restoredCount = this.originalMainSlides.length;
          this.sliderOptions.loop = (restoredCount > 1);

          /* Update product class based on restored slide count */
          this._updateProductMediaClasses(restoredCount);

          /* Reinitialize slider if needed */
          if (this.enableDesktopSlider || FoxTheme.config.mqlMobile) {
            this.initSlider();
            
            /* Wait for Swiper to finish initialization and layout calculations */
            requestAnimationFrame(() => {
              if (this.sliderInstance?.slider) {
                /* Update slider to ensure all calculations are complete */
                this.sliderInstance.slider.update();
                
                /* Wait one more frame for update to complete */
                requestAnimationFrame(() => {
                  /* Show/hide navigation arrows based on slide count */
                  this.toggleNavigationArrows(restoredCount);
                });
              }
            });
          }
        } else {
          console.warn('⚠️ [Variant Filter] mediaList or originalMainSlides not found in showAllMedia');
        }

        /* Restore all thumbnails */
        if (this.elements.thumbnails && this.originalThumbnails) {
          /* Destroy thumbnail Swiper if it exists */
          if (this.thumbsInstance?.slider) {
            this.thumbsInstance.slider.destroy(true, true);
            this.thumbsInstance = false;
          }

          /* Get thumbnail wrapper */
          const thumbnailWrapper = this.elements.thumbnails.querySelector('.swiper-wrapper');
          if (thumbnailWrapper) {
            /* Remove all current thumbnails */
            thumbnailWrapper.innerHTML = '';

            /* Add back all original thumbnails */
            this.originalThumbnails.forEach((originalThumb) => {
              const clonedThumb = originalThumb.cloneNode(true);
              thumbnailWrapper.appendChild(clonedThumb);
            });

            /* Re-attach click handlers to cloned thumbnails */
            const restoredThumbnails = thumbnailWrapper.querySelectorAll('.product__thumbs-item');
            restoredThumbnails.forEach((thumb) => {
              thumb.addEventListener('click', (e) => {
                e.preventDefault();
                const mediaId = parseInt(thumb.dataset.mediaId || thumb.dataset.target);
                if (mediaId && this.sliderInstance?.slider) {
                  /* Find the slide with this media ID */
                  const slides = this.sliderInstance.slider.slides;
                  for (let i = 0; i < slides.length; i++) {
                    const slide = slides[i];
                    if (!slide.classList.contains('swiper-slide-duplicate')) {
                      const mediaItem = slide.querySelector('.product__media-item');
                      if (mediaItem && parseInt(mediaItem.dataset.mediaId) === mediaId) {
                        this._navigateSlider(this.sliderInstance.slider, i);
                        this.syncThumbnailActiveState(mediaId);
                        break;
                      }
                    }
                  }
                }
              });
            });

            /* Reinitialize thumbnail slider if main slider exists */
            if (this.sliderInstance && (this.enableDesktopSlider || this.enableMobileThumbnails)) {
              this.initThumbsSlider();
              /* Reconnect thumbs to main slider */
              if (this.sliderInstance.slider && this.thumbsInstance?.slider) {
                this.sliderInstance.slider.params.thumbs = {
                  swiper: this.thumbsInstance.slider,
                  autoScrollOffset: 2,
                };
                this.sliderInstance.slider.thumbs.init();
                this.sliderInstance.slider.thumbs.update();
              }
            }
          }
        } else {
          console.warn('⚠️ [Variant Filter] thumbnails or originalThumbnails not found in showAllMedia');
        }
        
        /* Update image zoom dataSource after restoring all media */
        if (this.enableImageZoom && this.lightbox) {
          requestAnimationFrame(() => {
            this.updateImageZoomDataSource();
          });
        }
      }

      showFirstSlideOnly() {
        /* Reset filtered variant tracking */
        this.currentFilteredVariant = null;

        this._storeOriginalSlides();

        /* Destroy both sliders first */
        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (this.thumbsInstance?.slider) {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }

        /* Show only first slide */
        if (this.elements.mediaList && this.originalMainSlides && this.originalMainSlides.length > 0) {
          /* Remove all current slides */
          this.elements.mediaList.innerHTML = '';

          /* Add only the first slide */
          const firstSlide = this.originalMainSlides[0].cloneNode(true);
          this.elements.mediaList.appendChild(firstSlide);
        }

        /* Show only first thumbnail */
        if (this.elements.thumbnails && this.originalThumbnails && this.originalThumbnails.length > 0) {
          const thumbnailWrapper = this.elements.thumbnails.querySelector('.swiper-wrapper');
          if (thumbnailWrapper) {
            /* Remove all current thumbnails */
            thumbnailWrapper.innerHTML = '';

            /* Add only the first thumbnail */
            const firstThumb = this.originalThumbnails[0].cloneNode(true);
            thumbnailWrapper.appendChild(firstThumb);
          }
        }

        /* Reinitialize with single slide */
        this.reinitializeSliders(1);
      }

      filterInitialMedia() {
        /* Wait for variant-selects to be ready */
        const checkVariant = () => {
          const selectedVariantEl = document.querySelector('variant-selects [data-selected-variant]');
          if (selectedVariantEl) {
            try {
              const selectedVariant = JSON.parse(selectedVariantEl.textContent);
              if (selectedVariant && selectedVariant.id) {
                this.filterThumbnailsByVariant(selectedVariant.id);
              } else {
                console.warn('⚠️ [Variant Filter] Initial variant has no ID');
              }
            } catch (e) {
              console.warn('❌ [Variant Filter] Could not parse selected variant for media filtering:', e);
            }
          } else {
            /* Retry after a short delay if variant-selects not ready yet */
            setTimeout(checkVariant, 100);
          }
        };
        checkVariant();
      }
    }
  );
}
